// Action bars for Denied & underpaid (rcm-30). One primary, at most one secondary,
// then text links. Anything that moves money or leaves the practice opens an inline
// step in the same place; its button names the action and the amount. After the
// step, the bar is replaced by a result card.
import { useState } from 'react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { Claim, Outcome } from '../types';
import { billedOf, canBillThePatient, clockNow, money, noBillReason } from '../utils';
import { isMail, ruleForPayerName } from '../payerRules';
import { getAppealSettings, getSentRowSync, sendBlockedReason, shortOf, signerFor } from '../store';
import { Check, Spinner } from './common';
import { PMS } from '@/features/shared/pms';
import { hasMoneyPermission, MONEY_APPROVAL_NOTE } from '@/features/shared/permissions';

export interface ActionHandlers {
  onSend: () => void;
  onPreview: () => void;
  onResend: () => void;
  onResolve: (outcome: Outcome, reason: string, note: string) => void;
  onHaveElvaCall: () => void;
}

type Step =
  | { kind: 'send' }
  | { kind: 'bill' }
  | { kind: 'hold' }
  | { kind: 'stop'; opener: StopOpener }
  | null;

/** Hold reasons for an appeal ELVA would otherwise send today (fixed set, D127). */
const HOLD_REASONS = [
  'Waiting for a document',
  'The dentist wants to review it',
  'Payer is changing its rules',
  'Something else',
] as const;

/** The inline step behind "Hold it": pick a reason, then hold. */
function HoldStep({ claim, onHold, onCancel }: { claim: Claim; onHold: (reason: string) => void; onCancel: () => void }) {
  const [reason, setReason] = useState<string>(HOLD_REASONS[0]);
  return (
    <section className="apl-act-confirm apl-act-stop" aria-label="Hold this appeal">
      <p className="apl-act-sentence">
        <strong>Hold {claim.patient}’s appeal to {claim.payer.short}?</strong>{' '}
        <span className="apl-act-sub">ELVA won’t send it today. Why?</span>
      </p>
      <div className="apl-act-pills" role="radiogroup" aria-label="Why hold it">
        {HOLD_REASONS.map((r) => (
          <button
            key={r}
            type="button"
            role="radio"
            aria-checked={reason === r}
            className={`apl-act-pill${reason === r ? ' on' : ''}`}
            onClick={() => { setReason(r); }}
          >
            {r}
          </button>
        ))}
      </div>
      <div className="apl-act-row">
        <button type="button" className="apl-btn apl-btn-primary apl-btn-lg" onClick={() => { onHold(reason); }}>
          Hold it
        </button>
        <button type="button" className="apl-act-link" onClick={onCancel}>Cancel</button>
      </div>
    </section>
  );
}

export type StopOpener = 'accept' | 'dont_appeal' | 'dont_resend' | 'write_off';

/** The inline confirm (rcm-30 §2): a bold sentence, the named button, then Cancel. */
export function ConfirmStep({
  sentence,
  detail,
  confirmLabel,
  onConfirm,
  onCancel,
}: {
  sentence: ReactNode;
  detail?: ReactNode;
  confirmLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <section className="apl-act-confirm" aria-label="Confirm">
      <p className="apl-act-sentence">
        <strong>{sentence}</strong>
        {detail ? <> {detail}</> : null}
      </p>
      <div className="apl-act-row">
        <button type="button" className="apl-btn apl-btn-primary apl-btn-lg" onClick={onConfirm}>
          {confirmLabel}
        </button>
        <button type="button" className="apl-act-link" onClick={onCancel}>Cancel</button>
      </div>
    </section>
  );
}

/** The result card that replaces the bar (rcm-30 §2). Green = done; teal = moved on. */
export function AfterCard({
  tone,
  title,
  children,
}: {
  tone: 'done' | 'moved';
  title: ReactNode;
  children?: ReactNode;
}) {
  return (
    <section className={`apl-act-after ${tone}`} role="status">
      <span className="apl-act-mark" aria-hidden="true">
        {tone === 'done' ? <Check /> : '→'}
      </span>
      <div>
        <div className="apl-act-title">{title}</div>
        {children}
      </div>
    </section>
  );
}

const OPENER_SENTENCE: Record<StopOpener, string> = {
  accept: 'Accept what the payer paid',
  dont_appeal: 'Don’t appeal',
  dont_resend: 'Don’t resend',
  write_off: 'Write it off',
};

/**
 * The shared stop step (rcm-30 §4). Opened by Accept, Don't appeal, Don't resend
 * and Write it off. Choices the contract rules out stay visible, dashed, with the reason.
 */
export function StopStep({
  claim,
  opener,
  amount,
  onResolve,
  onCancel,
}: {
  claim: Claim;
  opener: StopOpener;
  amount: number;
  onResolve: ActionHandlers['onResolve'];
  onCancel: () => void;
}) {
  // D245: Write it off and The patient will pay it are hidden (not refused) without the permission.
  const moneyOk = hasMoneyPermission();
  const canBill = canBillThePatient(claim);
  const reason = noBillReason(claim);
  const first = claim.firstName;
  const short = claim.payer.short;
  const m = money(amount);
  // Payer's mistake and the contract rules billing out: one choice, so skip the pills (§4).
  const onlyWriteOff = moneyOk && opener === 'accept' && !canBill;
  const [choice, setChoice] = useState<Outcome>(moneyOk ? 'write_off' : 'other_insurance');
  const [note, setNote] = useState('');
  const needsNote = !onlyWriteOff && choice === 'other';
  const valid = !needsNote || note.trim() !== '';

  const buttonLabel = (() => {
    if (onlyWriteOff || choice === 'write_off') return `Write off ${m}`;
    if (choice === 'patient') return `Move ${m} to ${first}’s balance`;
    if (choice === 'other_insurance') return `Bill ${m} to another insurance`;
    return `Resolve ${m}`;
  })();

  const sentenceFor = (outcome: Outcome): string => {
    if (outcome === 'write_off') {
      if (opener === 'accept' && !canBill) {
        return `Written off ${m}. ${short} made the mistake, so ${first} isn’t billed.`;
      }
      if (opener === 'dont_appeal') return `Not appealed. Written off ${m}.`;
      if (opener === 'dont_resend') return `Not resent. Written off ${m}.`;
      return `Written off ${m}.`;
    }
    if (outcome === 'patient') return `Moved to ${first}’s balance in ${PMS}.`;
    if (outcome === 'other_insurance') return `Billed to another insurance · ${m}.`;
    return `${m} resolved: ${note.trim()}.`;
  };

  const confirm = () => {
    const outcome = onlyWriteOff ? 'write_off' : choice;
    onResolve(outcome, sentenceFor(outcome), note.trim());
  };

  const pills: { id: Outcome; label: string; disabled?: boolean }[] = [
    ...(moneyOk
      ? [
          { id: 'write_off' as const, label: 'Write it off' },
          canBill
            ? { id: 'patient' as const, label: 'The patient will pay' }
            : { id: 'patient' as const, label: 'The patient will pay · not allowed by this contract', disabled: true },
        ]
      : []),
    { id: 'other_insurance', label: 'Billed to another insurance' },
    { id: 'other', label: 'Something else' },
  ];

  return (
    <section className="apl-act-confirm apl-act-stop" aria-label={`What happens to the ${m}?`}>
      <p className="apl-act-sentence">
        <strong>What happens to the {m}?</strong>{' '}
        <span className="apl-act-sub">
          {claim.patient} · {short} · {OPENER_SENTENCE[opener]}
        </span>
      </p>
      {onlyWriteOff ? (
        <p className="apl-act-sentence">
          <strong>Write off {m}?</strong>{' '}
          <span>It’s {short}’s mistake, so it can’t be billed to {first}. Add a note if you like.</span>
        </p>
      ) : (
        <div className="apl-act-pills" role="radiogroup" aria-label="What happens to the money">
          {pills.map((p) => (
            <button
              key={p.id}
              type="button"
              role="radio"
              aria-checked={choice === p.id}
              disabled={p.disabled === true}
              className={`apl-act-pill${choice === p.id ? ' on' : ''}${p.disabled ? ' dis' : ''}`}
              onClick={() => { setChoice(p.id); }}
            >
              {p.label}
            </button>
          ))}
        </div>
      )}
      {!moneyOk ? <p className="apl-meta" style={{ margin: 0 }}>{MONEY_APPROVAL_NOTE}</p> : null}
      {moneyOk && !canBill && reason ? <p className="apl-meta" style={{ margin: 0 }}>{reason}</p> : null}
      {onlyWriteOff || needsNote ? (
        <textarea
          className="apl-textarea apl-act-note"
          aria-label="Note"
          placeholder={needsNote ? 'Required: what happened' : 'Note (optional)'}
          value={note}
          onChange={(e) => { setNote(e.target.value); }}
        />
      ) : null}
      <div className="apl-act-row">
        <button type="button" className="apl-btn apl-btn-primary apl-btn-lg" disabled={!valid} onClick={confirm}>
          {buttonLabel}
        </button>
        <button type="button" className="apl-act-link" onClick={onCancel}>Cancel</button>
      </div>
    </section>
  );
}

function appealDocs(claim: Claim): { docs: number; pages: number } {
  const extra = claim.appealKind === 'patient' ? 1 : 0;
  const packet = claim.packet ?? [];
  return {
    docs: packet.length + extra,
    pages: packet.reduce((s, d) => s + d.pages, 0) + extra,
  };
}

function viaName(claim: Claim): string {
  const rule = ruleForPayerName(claim.payer.name);
  const first = rule.order[0] ?? rule.accepts[0] ?? 'certified_mail';
  if (first === 'website' || first === 'api' || first === 'secure_message') return rule.firstDestName;
  if (first === 'fax') return 'fax';
  if (first === 'secure_email') return 'secure email';
  if (isMail(first)) return 'certified mail';
  return rule.firstDestName;
}

/** Everything under the recommendation: the bar, the open step, or the result card. */
export function ActionArea({ claim, h }: { claim: Claim; h: ActionHandlers }) {
  const [step, setStep] = useState<Step>(null);
  const [heldFor, setHeldFor] = useState<string | null>(null);
  if (claim.removed || claim.resolution || claim.reprocessRef) {
    return <ResolvedCard claim={claim} />;
  }
  if (claim.answer === 'needs_you') return null; // the decision card is the action here
  if (heldFor != null) {
    return (
      <AfterCard tone="moved" title="Held. ELVA won’t send this appeal today.">
        <p className="apl-act-line">
          Reason: {heldFor} · today {clockNow()}.{' '}
          <button type="button" className="apl-act-link" onClick={() => { setHeldFor(null); }}>Let ELVA send it</button>
        </p>
      </AfterCard>
    );
  }
  if (step?.kind === 'hold') {
    return (
      <HoldStep
        claim={claim}
        onHold={(r) => { setHeldFor(r); setStep(null); }}
        onCancel={() => { setStep(null); }}
      />
    );
  }
  if (step?.kind === 'send') {
    const { docs, pages } = appealDocs(claim);
    const signer = signerFor(claim);
    return (
      <ConfirmStep
        sentence={`Send to ${claim.payer.short} by ${viaName(claim)}, signed by ${signer}?`}
        detail={`${String(docs)} documents, ${String(pages)} pages.`}
        confirmLabel="Send it"
        onConfirm={h.onSend}
        onCancel={() => { setStep(null); }}
      />
    );
  }
  if (step?.kind === 'bill') {
    const m = money(claim.billAmount ?? billedOf(claim.procedures));
    return (
      <ConfirmStep
        sentence={`Move ${m} to ${claim.firstName}’s balance in ${PMS}?`}
        detail="ELVA Core collects it from there."
        confirmLabel="Move it"
        onConfirm={() => { h.onResolve('patient', `Moved to ${claim.firstName}’s balance in ${PMS}.`, ''); }}
        onCancel={() => { setStep(null); }}
      />
    );
  }
  if (step?.kind === 'stop') {
    const amount = claim.kind === 'underpaid' ? shortOf(claim) : (claim.billAmount ?? billedOf(claim.procedures));
    return (
      <StopStep
        claim={claim}
        opener={step.opener}
        amount={amount}
        onResolve={h.onResolve}
        onCancel={() => { setStep(null); }}
      />
    );
  }

  if (claim.answer === 'appeal') return <AppealBar claim={claim} h={h} onStep={setStep} />;
  if (claim.answer === 'resend') {
    return (
      <div className="apl-actionbar">
        <button type="button" className="apl-btn apl-btn-primary apl-btn-lg" onClick={h.onResend}>Resend the claim</button>
        <button type="button" className="apl-act-link" onClick={() => { setStep({ kind: 'stop', opener: 'dont_resend' }); }}>
          Don’t resend
        </button>
        <span className="apl-actionbar-meta">ELVA attached {claim.resendAttached ?? 'the missing record'}.</span>
      </div>
    );
  }
  if (claim.answer === 'bill_patient') {
    const canBill = canBillThePatient(claim);
    const m = money(claim.billAmount ?? billedOf(claim.procedures));
    // D245: Bill the patient and Write it off are hidden without the permission; the note stands in once.
    if (!hasMoneyPermission()) {
      return (
        <div className="apl-actionbar">
          <span className="apl-actionbar-meta" style={{ marginLeft: 0 }}>{MONEY_APPROVAL_NOTE}</span>
        </div>
      );
    }
    return (
      <div className="apl-actionbar">
        {canBill ? (
          <button type="button" className="apl-btn apl-btn-primary apl-btn-lg" onClick={() => { setStep({ kind: 'bill' }); }}>
            Bill {claim.firstName} {m}
          </button>
        ) : null}
        <button type="button" className="apl-act-link" onClick={() => { setStep({ kind: 'stop', opener: 'write_off' }); }}>
          Write it off
        </button>
        <span className="apl-actionbar-meta">
          {canBill
            ? claim.barReason ?? `${claim.firstName} owes this.`
            : noBillReason(claim) ?? `${claim.payer.short}’s contract doesn’t allow billing ${claim.firstName} for this.`}
        </span>
      </div>
    );
  }
  return <ReprocessBar claim={claim} h={h} onStop={(opener) => { setStep({ kind: 'stop', opener }); }} />;
}

function AppealBar({
  claim,
  h,
  onStep,
}: {
  claim: Claim;
  h: ActionHandlers;
  onStep: (s: Step) => void;
}) {
  const settings = getAppealSettings();
  const blocked = sendBlockedReason(claim, settings);
  const meta = blocked ?? `ELVA has you confirm each appeal while it learns — ${String(settings.confirmed)} of 20 confirmed.`;
  if (settings.autopilot) {
    return (
      <div className="apl-actionbar">
        <span className="apl-actionbar-meta" style={{ marginLeft: 0, fontWeight: 600, color: '#0b6663' }}>
          {blocked ?? 'ELVA sends this today at 4:00 PM'}
        </span>
        <button type="button" className="apl-btn apl-btn-sec" onClick={() => { onStep({ kind: 'hold' }); }}>Hold it</button>
        <button type="button" className="apl-act-link" onClick={h.onPreview}>Preview the packet</button>
        <button type="button" className="apl-act-link" onClick={() => { onStep({ kind: 'stop', opener: 'dont_appeal' }); }}>
          Don’t appeal
        </button>
        <span className="apl-actionbar-meta">On autopilot. ELVA sends appeals as soon as they’re ready.</span>
      </div>
    );
  }
  return (
    <div className="apl-actionbar">
      <button
        type="button"
        className="apl-btn apl-btn-primary apl-btn-lg"
        disabled={blocked != null}
        {...(blocked ? { title: blocked } : {})}
        onClick={() => { onStep({ kind: 'send' }); }}
      >
        Send the appeal
      </button>
      <button type="button" className="apl-btn apl-btn-sec" onClick={h.onPreview}>Preview the packet</button>
      <button type="button" className="apl-act-link" onClick={() => { onStep({ kind: 'stop', opener: 'dont_appeal' }); }}>
        Don’t appeal
      </button>
      <span className="apl-actionbar-meta">{meta}</span>
    </div>
  );
}

function ReprocessBar({
  claim,
  h,
  onStop,
}: {
  claim: Claim;
  h: ActionHandlers;
  onStop: (opener: StopOpener) => void;
}) {
  const [calling, setCalling] = useState(false);
  const [startedAt, setStartedAt] = useState('');
  const start = () => {
    setStartedAt(clockNow());
    setCalling(true);
    setTimeout(() => { h.onHaveElvaCall(); }, 1500);
  };
  return (
    <div className="apl-actionbar">
      <button type="button" className="apl-btn apl-btn-primary apl-btn-lg" disabled={calling} onClick={start}>
        {calling ? (
          <Spinner label={`ELVA is calling ${claim.payer.short} · started ${startedAt}`} />
        ) : (
          `Have ELVA call ${claim.payer.short}`
        )}
      </button>
      {/* Accepting the payment ends in a write-off or a patient balance: a money action (D245). */}
      {hasMoneyPermission() ? (
        <button type="button" className="apl-act-link" onClick={() => { onStop('accept'); }}>
          Accept what {claim.payer.short} paid
        </button>
      ) : null}
      <span className="apl-actionbar-meta">
        Changing a payment at {claim.payer.short} always waits for you. ELVA makes the call.
        {hasMoneyPermission() ? '' : ` ${MONEY_APPROVAL_NOTE}`}
      </span>
    </div>
  );
}

/** The result card for whatever the last action was (rcm-30 §2–§4). */
export function ResolvedCard({ claim }: { claim: Claim }) {
  const r = claim.resolution;
  if (claim.reprocessRef) {
    return (
      <AfterCard tone="done" title={`${claim.payer.short} agreed to reprocess at ${claim.provider}’s network fee.`}>
        <p className="apl-act-line">
          Reference {claim.reprocessRef} · call recording, 4:12 · new payment expected by Oct 24. If it doesn’t arrive, ELVA follows up like any unpaid claim.
        </p>
      </AfterCard>
    );
  }
  if (claim.removed === 'resent') {
    return (
      <AfterCard tone="moved" title={`In Claims to review, with ${claim.resendAttached ?? 'the record'} attached.`}>
        <p className="apl-act-line">
          It goes to {claim.payer.short} once it’s confirmed there. <Link to="/claims" className="apl-act-link">Open in Claims</Link>
        </p>
      </AfterCard>
    );
  }
  if (claim.removed === 'sent') {
    const row = getSentRowSync(claim.id);
    return (
      <AfterCard tone="done" title={`Sent today, ${r?.at ?? clockNow()}, by ${r?.via ?? claim.payer.short}.`}>
        <p className="apl-act-line">
          {row?.happening ?? ''} <Link to="/denied/appeals" className="apl-act-link">Open in Appeals sent</Link>
        </p>
      </AfterCard>
    );
  }
  if (claim.decisionRecord && !claim.removed) {
    return (
      <AfterCard tone="moved" title="ELVA is preparing the appeal.">
        <p className="apl-act-line">
          {claim.decisionRecord.recorded} — recorded by {claim.decisionRecord.by}, today {claim.decisionRecord.at}. The claim moves to Appeal ready.
        </p>
      </AfterCard>
    );
  }
  if (r) {
    return (
      <AfterCard tone="done" title={r.reason}>
        <p className="apl-act-line">
          Today, {r.at} · {r.by}. The claim moves to Resolved.{' '}
          <Link to={`/claims/record/${claim.id}`} className="apl-act-link">Open the claim record</Link>
        </p>
      </AfterCard>
    );
  }
  return null;
}
